import { setSignInErrorMessage } from '@/store';

export class RequestResult<TData> {
  public static createSuccessful<TData>(data: TData): RequestResult<TData> {
    setSignInErrorMessage('');

    return new RequestResult(true, data);
  }

  public static createUnsuccessful<T>(errorMessage: string) {
    setSignInErrorMessage(errorMessage);

    return new RequestResult<T>(false, undefined, errorMessage);
  }

  private readonly requestData?: TData;
  private readonly errorMessage?: string;

  private constructor(
    private readonly isSuccessful: boolean,
    requestData?: TData,
    errorMessage?: string,
  ) {
    this.requestData = requestData;
    this.errorMessage = errorMessage;
  }

  public get isSuccess() {
    return this.isSuccessful;
  }

  public get data() {
    if (!this.isSuccessful) {
      throw new Error('Cannot access data when result is unsuccessful.');
    }

    return this.requestData;
  }

  public get error() {
    if (this.isSuccessful) {
      throw new Error('Cannot access error when result is successful.');
    }

    return this.errorMessage;
  }

  public match<T>(onSuccess: (data: TData) => T, onError: (errorMessage: string) => T): T {
    return this.isSuccessful ? onSuccess(this.data!) : onError(this.error!);
  }
}

export const requestWrapper = async (
  request: () => Promise<Response>,
  errorMessage: string,
  parse = true,
) => {
  try {
    const response = await request();
    const parsedResponse = parse ? await response.json() : response;

    return response.ok
      ? RequestResult.createSuccessful(parsedResponse)
      : RequestResult.createUnsuccessful(errorMessage);
  } catch (e) {
    return RequestResult.createUnsuccessful(errorMessage);
  }
};
